import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { CheckSquare, ArrowRight, GraduationCap } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";

interface MyCohortItem {
  cohort: {
    id: number;
    name: string;
    status: string;
    startDate: string;
    endDate: string;
  };
  program: {
    name: string;
    programType: string;
  };
  member: {
    role: string;
    status: string;
    totalTasksCompleted: number;
    qualityScore: number | null;
    enrolledAt: string;
  };
  completionPercentage: number;
}

const programTypeColors: Record<string, string> = {
  volunteer: "bg-teal-500/10 text-teal-700 dark:text-teal-400",
  volunteer_internship: "bg-teal-500/10 text-teal-700 dark:text-teal-400",
  angel_club: "bg-purple-500/10 text-purple-700 dark:text-purple-400",
  lp_club: "bg-blue-500/10 text-blue-700 dark:text-blue-400",
  accelerator: "bg-orange-500/10 text-orange-700 dark:text-orange-400",
  custom: "bg-muted text-muted-foreground",
};

const programTypeLabels: Record<string, string> = {
  volunteer: "Volunteer",
  volunteer_internship: "Volunteer Internship",
  angel_club: "Angel Club",
  lp_club: "LP Club",
  accelerator: "Accelerator",
  custom: "Custom",
};

function formatProgramType(raw: string): string {
  return raw.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

function getStatusVariant(status: string): "default" | "secondary" | "outline" {
  switch (status) {
    case "active":
      return "default";
    case "completed":
      return "secondary";
    default:
      return "outline";
  }
}

export function MyCohorts() {
  const { data: cohorts, isLoading } = useQuery<MyCohortItem[]>({
    queryKey: ["/api/my/cohorts"],
  });

  if (isLoading) {
    return (
      <section className="mb-8" data-testid="section-my-programs">
        <div className="mb-6">
          <Skeleton className="h-7 w-40" />
          <Skeleton className="h-4 w-56 mt-1" />
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2].map((i) => (
            <Card key={i} data-testid={`skeleton-cohort-${i}`}>
              <CardContent className="pt-6 space-y-3">
                <Skeleton className="h-5 w-20" />
                <Skeleton className="h-6 w-3/4" />
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-9 w-28" />
              </CardContent>
            </Card>
          ))}
        </div>
      </section>
    );
  }

  return (
    <section className="mb-8" data-testid="section-my-programs">
      <div className="mb-6">
        <h2 className="font-heading text-xl sm:text-2xl font-bold text-foreground">
          My Programs
        </h2>
        <p className="text-muted-foreground">
          Your active cohorts and program progress
        </p>
      </div>

      {!cohorts || cohorts.length === 0 ? (
        <Card data-testid="empty-my-programs">
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <GraduationCap className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
              <p className="text-muted-foreground">No active programs</p>
              <p className="text-sm text-muted-foreground mt-1">
                Explore available programs and apply to get started.
              </p>
              <Button variant="outline" className="mt-4" asChild>
                <Link href="/cohorts/apply" data-testid="link-apply-cohorts">
                  Browse Programs
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {cohorts.map((item) => {
            const typeColor = programTypeColors[item.program.programType] || programTypeColors.custom;
            const typeLabel = programTypeLabels[item.program.programType] || formatProgramType(item.program.programType);

            return (
              <Card key={item.cohort.id} className="hover-elevate" data-testid={`card-cohort-${item.cohort.id}`}>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <Badge
                      variant="secondary"
                      className={`no-default-active-elevate ${typeColor}`}
                      data-testid={`badge-program-type-${item.cohort.id}`}
                    >
                      {typeLabel}
                    </Badge>
                    <Badge
                      variant={getStatusVariant(item.cohort.status)}
                      className="no-default-active-elevate capitalize"
                      data-testid={`badge-cohort-status-${item.cohort.id}`}
                    >
                      {item.cohort.status}
                    </Badge>
                  </div>

                  <h3 className="font-semibold text-foreground mb-1" data-testid={`text-cohort-name-${item.cohort.id}`}>
                    {item.cohort.name}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-3" data-testid={`text-program-name-${item.cohort.id}`}>
                    {item.program.name}
                  </p>

                  <div className="mb-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-muted-foreground">Progress</span>
                      <span className="text-xs font-medium text-foreground" data-testid={`text-completion-${item.cohort.id}`}>
                        {item.completionPercentage}%
                      </span>
                    </div>
                    <Progress
                      value={item.completionPercentage}
                      className="h-2"
                      data-testid={`progress-cohort-${item.cohort.id}`}
                    />
                  </div>

                  <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4 flex-wrap" data-testid={`stats-cohort-${item.cohort.id}`}>
                    <span className="flex items-center gap-1">
                      <CheckSquare className="h-3 w-3" />
                      {item.member.totalTasksCompleted ?? 0} tasks
                    </span>
                  </div>

                  <Button size="sm" variant="outline" asChild data-testid={`button-open-portal-${item.cohort.id}`}>
                    <Link href={`/cohort/${item.cohort.id}`}>
                      Open Portal
                      <ArrowRight className="ml-1 h-3 w-3" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </section>
  );
}
